import {
  useEventCreate,
  EventCreate as EventCreateDto,
  isTask,
  useRequiredConfig,
  stoppageDefaultObject
} from '@domain/shifthandover'
import {PermissionType} from '@hconnect/apiclient'
import {
  EventType,
  AttachmentFile,
  ParticularShift,
  StoppageKind,
  ShiftEvent,
  BaseEvent,
  Task
} from '@hconnect/common/types'
import {LoadingSwitcher} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import {checkPermission, usePermission, usePermissionsParsedQuery} from '@hproduce/permissions'
import {EventAttachmentUpload} from '@widgets/common-shift-event'
import React, {useCallback, useEffect, useState} from 'react'
import {v4 as uuid} from 'uuid'

import {useCurrentShift} from '../hooks/useCurrentShift'
import {type ChecklistsInputSettings} from '../types'

import {parameterChangeDefaultObject} from './eventForm/ParameterChangeForm'
import {eventDefaultObject} from './eventForm/SharedEventForm'
import {systemChangeRequestDefaultObject} from './eventForm/SystemChangeRequestForm'
import {taskDefaultObject} from './eventForm/TaskForm'
import {EventFormContainer} from './EventFormContainer'
import {EventTypeSelectBox} from './eventProperties/EventTypeSelectBox'

const createNew = (
  eventType: EventType,
  currentShift: ParticularShift,
  initialData: Partial<ShiftEvent> = {}
): Partial<ShiftEvent> => {
  switch (eventType) {
    case StoppageKind.Incident:
    case StoppageKind.Circumstance:
    case StoppageKind.PlannedStoppage:
    case StoppageKind.Other:
      return {
        ...stoppageDefaultObject<Partial<ShiftEvent>>(initialData),
        eventType
      } as Partial<ShiftEvent>

    case 'task': {
      const temp = taskDefaultObject()
      if (isTask(temp as BaseEvent)) {
        ;(temp as Task).dueDate = currentShift.endDate
      }
      return temp
    }

    case 'systemChangeRequest':
      return systemChangeRequestDefaultObject()
    case 'parameterChange':
      return parameterChangeDefaultObject()

    case 'spot':
      throw new Error('Spot events should not be created, only viewed/edited')

    default:
      return eventDefaultObject(eventType, initialData)
  }
}

type Props = {
  'data-test-id'?: string
  eventTypeLabelPrefix?: string
  initialData?: Partial<ShiftEvent>
  preSetEventType?: EventType
  hideStoppageDateType?: boolean
  checklistsInputSettings?: ChecklistsInputSettings
  plantId: string
  doClose(original?: ShiftEvent, newVersion?: ShiftEvent): void
  onSubmit?(item: ShiftEvent): void
  onSuccess?(data: ShiftEvent): void
  onCreationStart?: (uuid: string) => void
  onCreationFinish?: (uuid: string) => void
  skipOverlapValidation?: boolean
}

export const EventCreate: React.FC<Props> = ({
  doClose,
  onSubmit,
  onSuccess,
  preSetEventType,
  initialData,
  eventTypeLabelPrefix,
  hideStoppageDateType,
  checklistsInputSettings,
  plantId,
  onCreationStart,
  onCreationFinish,
  skipOverlapValidation,
  ...props
}) => {
  const {country, shifts, isEquipmentAvailable} = useRequiredConfig(plantId)
  const currentShift = useCurrentShift({plantId, shifts})
  const plantContext = {plantId, countryId: country}
  const timezone = useUpmPlantTimezone(plantId)
  const isMovieAttachmentEnabled = useCommonFeatureFlag(FeatureFlagKey.MovieAttachment)
  const [files, setFiles] = useState<Array<AttachmentFile>>([])
  const [eventCreationUuid, setEventCreationUuid] = useState<string | null>(null)

  const createAction = useEventCreate({
    plantId,
    timezone,
    options: {
      onSuccess
    }
  })
  const {data: permissions} = usePermissionsParsedQuery()
  const allowedToAddDocuments = usePermission({plantContext, permissionTypes: ['ADD_DOCUMENTS']})

  const hasPermissionChecker = useCallback(
    (permissionType: PermissionType) =>
      permissions
        ? checkPermission(permissionType, plantContext.plantId, permissions, [
            plantContext.countryId
          ])
        : false,
    [plantContext.plantId, plantContext.countryId, permissions]
  )
  const {data} = createAction

  useEffect(() => {
    if (data) {
      onSubmit?.(data)
    }
  }, [onSubmit, data])
  const preSelectedEventType = preSetEventType || initialData?.eventType
  const [item, setItem] = useState<Partial<ShiftEvent> | undefined>(
    (preSelectedEventType && createNew(preSelectedEventType, currentShift, initialData)) ||
      undefined
  )

  useEffect(() => {
    if (preSelectedEventType) {
      setItem(createNew(preSelectedEventType, currentShift, initialData))
    }
  }, [initialData, preSelectedEventType, currentShift])

  const onEventTypeSelect = useCallback(
    (eventType: EventType) => {
      const processUuid = uuid()
      onCreationStart?.(processUuid)
      setEventCreationUuid(processUuid)
      setItem(createNew(eventType, currentShift))
    },
    [currentShift, onCreationStart]
  )

  const doSubmit = React.useCallback(
    (item: EventCreateDto) => {
      createAction.mutate(
        {
          ...item,
          attachments: files
        },
        {
          onSuccess: () => {
            eventCreationUuid && onCreationFinish?.(eventCreationUuid)
          }
        }
      )
    },
    [createAction, files, eventCreationUuid, onCreationFinish]
  )

  return (
    <>
      <LoadingSwitcher isLoading={createAction.isPending}>
        {item ? (
          <EventFormContainer
            country={country}
            plantId={plantId}
            data-test-id={props['data-test-id']}
            isCreateMode={true}
            item={item}
            doClose={() => doClose()}
            doSubmit={doSubmit}
            eventTypeLabelPrefix={eventTypeLabelPrefix}
            hideStoppageDateType={hideStoppageDateType}
            checklistsInputSettings={checklistsInputSettings}
            isEquipmentAvailable={isEquipmentAvailable}
            skipOverlapValidation={skipOverlapValidation}
          >
            {allowedToAddDocuments && (
              <EventAttachmentUpload
                isMovieAttachmentEnabled={isMovieAttachmentEnabled}
                setAttachments={setFiles}
                attachments={files}
                hasPermission={hasPermissionChecker}
              />
            )}
          </EventFormContainer>
        ) : (
          <EventTypeSelectBox onSelect={onEventTypeSelect} doClose={doClose} />
        )}
      </LoadingSwitcher>
    </>
  )
}
